import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { usePlayerAuth } from "../context/PlayerAuthContext";
import { ACADEMY_LOGO } from "../data/assets";
import { User, ShieldCheck, Lock, Loader2, ArrowRight, Mail, KeyRound, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BG_IMAGE = "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const clubDest = () => {
  try {
    const info = JSON.parse(localStorage.getItem("clubCoachInfo") || "{}");
    return info.school_id ? "/institution-dashboard" : "/club-dashboard";
  } catch { return "/club-dashboard"; }
};

const ROLES = [
  { id: "player", label: "Player", icon: User },
  { id: "club", label: "Club / Institution", icon: ShieldCheck },
  { id: "coach", label: "Coach", icon: Lock },
];

export default function Login() {
  const navigate = useNavigate();
  const { login } = usePlayerAuth();
  const [role, setRole] = useState("player");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passcode, setPasscode] = useState("");
  const [remember, setRemember] = useState(localStorage.getItem("nexto_remember") === "1");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("login"); // login | forgot
  const [forgotSent, setForgotSent] = useState(false);
  const [ready, setReady] = useState(false);
  const emailRef = useRef(null);
  const passcodeRef = useRef(null);

  // Remember Me: if a session already exists, skip the portal entirely.
  useEffect(() => {
    if (localStorage.getItem("nexto_remember") === "1") {
      if (localStorage.getItem("coachToken")) { navigate("/coach", { replace: true }); return; }
      if (localStorage.getItem("clubCoachToken")) { navigate(clubDest(), { replace: true }); return; }
      if (localStorage.getItem("playerToken")) { navigate("/", { replace: true }); return; }
    }
    setReady(true);
  }, [navigate]);

  const rememberPersist = useCallback(() => {
    if (remember) localStorage.setItem("nexto_remember", "1");
    else localStorage.removeItem("nexto_remember");
  }, [remember]);

  // Auto-focus the first field as soon as the portal is ready (and on tab / mode changes).
  useEffect(() => {
    if (!ready) return;
    const t = setTimeout(() => {
      if (mode === "forgot") return;
      if (role === "coach") passcodeRef.current?.focus();
      else emailRef.current?.focus();
    }, 60);
    return () => clearTimeout(t);
  }, [ready, role, mode]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      if (role === "player") {
        const player = await login(email.trim().toLowerCase(), password);
        rememberPersist();
        toast.success(`Welcome back, ${player.display_name || player.name || "Player"}.`);
        navigate("/", { replace: true });
      } else if (role === "club") {
        const { data } = await axios.post(`${API}/auth/club-coach/login`, { email: email.trim().toLowerCase(), password });
        localStorage.setItem("clubCoachToken", data.access_token);
        localStorage.setItem("clubCoachInfo", JSON.stringify(data.coach || {}));
        rememberPersist();
        toast.success(`Welcome, ${data.coach?.name || "Coach"}.`);
        navigate(data.coach?.school_id ? "/institution-dashboard" : "/club-dashboard", { replace: true });
      } else {
        const { data } = await axios.post(`${API}/coach/login`, { passcode });
        localStorage.setItem("coachToken", data.access_token);
        rememberPersist();
        toast.success("Welcome, Coach.");
        navigate("/coach", { replace: true });
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setForgotSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not send reset link.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-black/40 border border-white/15 rounded-lg pl-11 pr-4 py-3.5 text-white text-sm placeholder:text-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24] focus-visible:border-transparent transition-all";

  if (!ready) return <div className="min-h-screen bg-[#0a0a0a]" />;

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#0a0a0a] text-white flex items-center justify-center px-4 py-10">
      {/* Cinematic Ken-Burns background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="kenburns-bg" style={{ backgroundImage: `url(${BG_IMAGE})` }} />
      </div>
      {/* Dark readability overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 15%, rgba(196,30,36,0.20), transparent 55%)" }} />

      <div className="relative z-10 w-full max-w-md login-rise" data-testid="login-portal">
        <div className="glass-card rounded-2xl p-7 sm:p-9 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
          {/* Branding */}
          <div className="flex flex-col items-center text-center mb-6">
            <button type="button" data-testid="login-logo" onClick={() => navigate("/")}
              className="rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24] transition-transform hover:scale-105" aria-label="Back to home">
              <img src={ACADEMY_LOGO} alt="The Next O Technical Academy" className="w-48 h-auto object-contain mb-3 drop-shadow-[0_4px_20px_rgba(212,175,55,0.35)]" />
            </button>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#C41E24]">Next O Technical Academy</div>
            <h1 className="font-headings text-3xl sm:text-4xl uppercase tracking-wide leading-none mt-1">
              {mode === "forgot" ? "Reset Access" : "Welcome Back"}
            </h1>
            <p className="text-sm text-white/50 mt-2">
              {mode === "forgot" ? "We'll email you a secure reset link." : "Welcome back to the Standard."}
            </p>
          </div>

          {mode === "login" ? (
            <>
              {/* Role tabs */}
              <div className="grid grid-cols-3 gap-1.5 p-1 bg-black/40 border border-white/10 rounded-xl mb-6" data-testid="login-role-tabs">
                {ROLES.map((r) => {
                  const active = role === r.id;
                  return (
                    <button key={r.id} type="button" data-testid={`login-tab-${r.id}`}
                      onClick={() => { setRole(r.id); setError(""); }}
                      className={`flex flex-col items-center justify-center gap-1 py-2.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${active ? "bg-[#C41E24] text-white shadow-lg" : "text-white/50 hover:text-white hover:bg-white/5"}`}>
                      <r.icon size={16} />
                      <span className="leading-tight text-center">{r.label}</span>
                    </button>
                  );
                })}
              </div>

              <form onSubmit={submit} data-testid="login-form" className="space-y-4">
                {role === "coach" ? (
                  <div className="relative">
                    <KeyRound size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35" />
                    <input data-testid="login-passcode" ref={passcodeRef} type="password" required autoFocus autoComplete="off" value={passcode}
                      onChange={(e) => setPasscode(e.target.value)} placeholder="Coach passcode" className={inputCls} />
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Mail size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35" />
                      <input data-testid="login-email" ref={emailRef} type="email" inputMode="email" autoComplete="email" required autoFocus value={email}
                        onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className={inputCls} />
                    </div>
                    <div className="relative">
                      <Lock size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35" />
                      <input data-testid="login-password" type={showPw ? "text" : "password"} autoComplete="current-password" required value={password}
                        onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={`${inputCls} pr-11`} />
                      <button type="button" onClick={() => setShowPw((s) => !s)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-white/35 hover:text-white/80 transition-colors" aria-label="Toggle password">
                        {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                      </button>
                    </div>
                  </>
                )}

                <div className="flex items-center justify-between text-xs">
                  <label className="flex items-center gap-2 text-white/55 cursor-pointer select-none" data-testid="login-remember">
                    <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                      className="w-4 h-4 rounded accent-[#C41E24] bg-black/40 border-white/20" />
                    Remember Me
                  </label>
                  {role !== "coach" && (
                    <button type="button" data-testid="login-forgot-link" onClick={() => { setMode("forgot"); setError(""); setForgotSent(false); }}
                      className="text-white/55 hover:text-[#C41E24] font-semibold transition-colors">Forgot Password?</button>
                  )}
                </div>

                {error && <div data-testid="login-error" className="text-sm text-[#ff6b6b] bg-[#C41E24]/10 border border-[#C41E24]/30 rounded-lg px-3 py-2.5">{error}</div>}

                <button data-testid="login-submit" type="submit" disabled={loading}
                  className="group w-full flex items-center justify-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white text-sm font-bold uppercase tracking-widest py-3.5 rounded-lg transition-all disabled:opacity-60 shadow-[0_10px_30px_rgba(196,30,36,0.4)]">
                  {loading ? <Loader2 size={17} className="animate-spin" /> : <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />}
                  {loading ? "Signing in…" : "Enter"}
                </button>
              </form>
            </>
          ) : forgotSent ? (
            <div data-testid="forgot-sent" className="text-center py-4">
              <div className="w-14 h-14 mx-auto rounded-full bg-[#22C55E]/15 border border-[#22C55E]/40 flex items-center justify-center mb-4">
                <Mail size={24} className="text-[#22C55E]" />
              </div>
              <p className="text-sm text-white/70 leading-relaxed">If an account exists for <b className="text-white">{email}</b>, a secure reset link is on its way. It expires in 60 minutes.</p>
              <button data-testid="forgot-back" onClick={() => { setMode("login"); setForgotSent(false); }}
                className="mt-6 text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white transition-colors">← Back to sign in</button>
            </div>
          ) : (
            <form onSubmit={submitForgot} data-testid="forgot-form" className="space-y-4">
              <div className="relative">
                <Mail size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35" />
                <input data-testid="forgot-email" type="email" inputMode="email" autoComplete="email" required autoFocus value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="Your account email" className={inputCls} />
              </div>
              {error && <div data-testid="forgot-error" className="text-sm text-[#ff6b6b] bg-[#C41E24]/10 border border-[#C41E24]/30 rounded-lg px-3 py-2.5">{error}</div>}
              <button data-testid="forgot-submit" type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white text-sm font-bold uppercase tracking-widest py-3.5 rounded-lg transition-all disabled:opacity-60">
                {loading ? <Loader2 size={17} className="animate-spin" /> : <Mail size={16} />}
                {loading ? "Sending…" : "Send Reset Link"}
              </button>
              <button type="button" data-testid="forgot-cancel" onClick={() => { setMode("login"); setError(""); }}
                className="w-full text-xs font-bold uppercase tracking-wider text-white/50 hover:text-white transition-colors">← Back to sign in</button>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-white/55 mt-6">
          Not a member yet?{" "}
          <button data-testid="login-view-tiers" onClick={() => navigate("/")}
            className="text-[#C41E24] font-bold hover:text-white transition-colors underline underline-offset-4 decoration-[#C41E24]/40">
            View Training Tiers
          </button>
        </p>
      </div>
    </div>
  );
}
